/* eslint-disable react/jsx-no-constructed-context-values */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  type ColumnFiltersState,
  type FilterFn,
  functionalUpdate,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type Row,
  type SortingState,
  type Table,
  type Updater,
  useReactTable,
} from '@tanstack/react-table';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import React, { createContext, type PropsWithChildren, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { type QueryKey, useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useDebounce } from '@/_metronic/helpers';
import { useDataTableCleanup } from '@/app/hooks/useDataTableCleanup';
import { type ExportRequestDto } from '@/app/models/ExportRequestDto';
import { type ExportFieldDef } from '@/app/modules/export/types';
import { type DataTableColumnDef } from '@/app/models/DataTableColumnDef';
import { type ExportScopeType } from '@/app/enums/ExportScopeType';
import DataTableBody from '@/app/modules/table/components/DataTableBody';
import DataTableControls from '@/app/modules/table/components/DataTableControls';
import DataTablePagination from '@/app/modules/table/components/DataTablePagination';
import { type ListData } from '@/app/models/ListData';
import { type DataTablePage } from '@/app/modules/table/core/DataTablePage';
import { type DataTablePaginationOptions } from '@/app/modules/table/core/DataTablePaginationOptions';
import {
  getDataTablePage,
  getDataTableQueryKey,
  useUrlFilter,
  useUrlTableState,
} from '@/app/modules/table/core/_helpers';
import { list } from '@/app/modules/table/core/_requests';
export { generateUrlFilterParam, getDataTableQueryKey } from '@/app/modules/table/core/_helpers';

dayjs.extend(isBetween);

declare module '@tanstack/react-table' {
  interface FilterFns {
    dateRangeFromLocalData: FilterFn<unknown>;
  }
}

export interface FilterItem {
  label: string;
  value: any;
}

interface DataTablePreferences {
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  pageSize?: number;
}

type DatatableProps<TItem extends object> = {
  data?: object[];
  adaptPage?: (response: ListData<TItem>) => DataTablePage;
  endpoint?: string;
  columns: DataTableColumnDef<any>[];
  perPage?: number;
  pagination?: DataTablePaginationOptions;
  direction?: 'asc' | 'desc';
  sort?: string;
  filter?: object;
  refreshKey?: number;
  getRowCanExpand?: (row: Row<object>) => boolean;
  getRowId?: (row: object) => string;
  renderSubComponent?: (row: Row<object>) => React.ReactNode;
  placeHolder?: React.ReactElement;
  searchPlaceHolder?: string;
  generalSearchColumns?: string[];
  exportableFormats?: string[];
  exportFields?: ExportFieldDef[];
  /** Colunas pré-marcadas quando um catálogo é compartilhado ou a tabela combina vários campos numa célula. */
  exportColumnKeys?: string[];
  exportColumnLabels?: Record<string, string>;
  /**
   * Export gerado no backend (`POST <exportEndpoint>`, colunas de `GET <exportEndpoint>/columns`),
   * com todas as linhas do filtro. Sem ele, o export é o da página em memória.
   */
  exportEndpoint?: string;
  /** Nome, no resumo dos filtros do arquivo, de cada campo `*_label` do filtro (`category_label: 'Categoria'`). */
  exportFilterLabels?: Record<string, string>;
  /**
   * As formas de exportação que o modal oferece, e a primeira da lista vem marcada; sem a prop, todas as que as
   * linhas permitem. Linha sem `id` (relatório agregado) só exporta Todas as páginas, com ou sem a prop.
   */
  exportScopes?: ExportScopeType[];
  isExportable?: boolean;
  isRefreshable?: boolean;
  isSearchable?: boolean;
  enableBarcodeSearch?: boolean;
  actionComponent?: React.ReactElement;
  bulkActionComponents?: React.ReactElement[];
  filterComponents?: React.ReactElement[];
  showControls?: boolean;
  showPagination?: boolean;
  hasMinHeight?: boolean;
  showSelectedRows?: boolean;
  tableKey?: string;
  queryKeyPrefix?: QueryKey;
  rowHeightMode?: 'compact' | 'regular';
  variant?: 'list' | 'report' | 'classic';
  showRowIndex?: boolean;
  onDataChange?: (data: object[]) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  onRowClick?: (row: Row<object>) => void;
};

type LocationState = {
  refreshDatatable?: boolean;
};

type DataTableContextProps<TFilter extends Record<string, any> = Record<string, any>> = {
  table?: Table<object>;
  data?: object;
  dataAdditionalContent?: unknown;
  pageInfo?: DataTablePage;
  pagination?: DataTablePaginationOptions;
  columns: DataTableColumnDef<object>[];
  searchTerm?: string;
  setSearchTerm: (value: string) => void;
  filter?: TFilter;
  setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
  placeHolder?: React.ReactElement;
  isLoading: boolean;
  error: unknown;
  setRefreshTimestamp: (value: number) => void;
  searchPlaceHolder: string;
  setFilter: React.Dispatch<React.SetStateAction<TFilter | undefined>>;
  updateFilter: (newFilter: Partial<TFilter>) => void;
  clearFilter: (filter: keyof TFilter) => void;
  refresh: () => void;
  tableKey: string;
  generalSearchColumns: string[];
  exportableFormats: string[];
  exportFields?: ExportFieldDef[];
  exportColumnKeys?: string[];
  exportColumnLabels?: Record<string, string>;
  exportEndpoint?: string;
  exportFilterLabels?: Record<string, string>;
  exportScopes?: ExportScopeType[];
  urlFilterLabels: Record<string, unknown>;
  /** Filtro, ordenação e busca que o `/list` recebe agora, para o export do backend mandar igual. */
  listQuery: ExportRequestDto['query'];
  isExportable: boolean;
  isRefreshable: boolean;
  isSearchable: boolean;
  enableBarcodeSearch: boolean;
  actionComponent?: React.ReactElement;
  bulkActionComponents?: React.ReactElement[];
  selectedRows: Row<object>[];
  selectedRowIds: string[];
  selectedRowsData: object[];
  clearSelection: () => void;
  filterComponents: React.ReactElement[];
  rowHeightMode: 'compact' | 'regular';
  variant: 'list' | 'report' | 'classic';
  showRowIndex: boolean;
  renderSubComponent?: (row: Row<object>) => React.ReactNode;
  onRowClick?: (row: Row<object>) => void;
};

const DataTableContext = createContext<DataTableContextProps<any> | null>(null);

//TODO remover daqui coocar em um arquivo utils
const dateRangeFilterFn: FilterFn<any> = (row, columnId, value: { start: Date; end: Date }) => {
  const date = dayjs(row.getValue<Date>(columnId));
  return date.isBetween(value.start, value.end, 'day', '[]');
};

const getDataTablePreferencesKey = (tableKey?: string): string => {
  return `datatable_${tableKey || 'default'}_prefs`;
};

const loadDataTablePreferences = (tableKey?: string): DataTablePreferences => {
  try {
    const key = getDataTablePreferencesKey(tableKey);
    const stored = localStorage.getItem(key);
    if (!stored) return {};

    const parsed = JSON.parse(stored) as unknown;
    const preferences: DataTablePreferences = {};

    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const obj = parsed as { [key: string]: unknown };
      if (typeof obj.sortColumn === 'string') {
        preferences.sortColumn = obj.sortColumn;
      }
      if (typeof obj.sortDirection === 'string' && (obj.sortDirection === 'asc' || obj.sortDirection === 'desc')) {
        preferences.sortDirection = obj.sortDirection;
      }
      if (typeof obj.pageSize === 'number') {
        preferences.pageSize = obj.pageSize;
      }
    }

    return preferences;
  } catch (error) {
    console.warn('Erro ao carregar preferências da tabela:', error);
    return {};
  }
};

const saveDataTablePreferences = (preferences: DataTablePreferences, tableKey?: string): void => {
  try {
    const key = getDataTablePreferencesKey(tableKey);
    localStorage.setItem(key, JSON.stringify(preferences));
  } catch (error) {
    console.warn('Erro ao salvar preferências da tabela:', error);
  }
};

const generateTableKey = (columns: DataTableColumnDef<any>[], endpoint?: string, tableKey?: string): string => {
  if (tableKey) return tableKey;

  // Gera uma chave baseada no endpoint e nas colunas
  const columnIds = columns.map((col) => col.id).join(',');
  const baseString = `${endpoint || 'localData'}_${columnIds}`;
  let hash = 0;
  for (let i = 0; i < baseString.length; i++) {
    const char = baseString.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0; // Converte para um inteiro de 32 bits
  }
  return `table_${Math.abs(hash)}`;
};

// Custom global filter that searches across multiple columns
const createMultiColumnGlobalFilter = (columns: string[], tableColumns: DataTableColumnDef<any>[]): FilterFn<any> => {
  // Get valid column IDs from table definition
  const validColumnIds = new Set(tableColumns.map((col) => col.id || col.accessorKey).filter(Boolean));

  return (row, _columnId, filterValue) => {
    const search = String(filterValue).toLowerCase();

    // If specific columns defined, filter to only valid ones
    // If no columns defined, use all valid column IDs
    let columnsToSearch: string[];

    if (columns.length > 0) {
      columnsToSearch = columns.filter((colId) => validColumnIds.has(colId));
    } else {
      columnsToSearch = Array.from(validColumnIds) as string[];
    }

    return columnsToSearch.some((colId) => {
      const value = row.getValue(colId) ?? row.original[colId];
      if (value == null) return false;
      return String(value).toLowerCase().includes(search);
    });
  };
};

const EMPTY_ADDITIONAL_CONTENT = {};
const EMPTY_QUERY_KEY_PREFIX: QueryKey = [];

const DataTable = <TItem extends object = object>({
  data: localData,
  adaptPage,
  columns,
  endpoint,
  getRowCanExpand,
  getRowId,
  renderSubComponent,
  placeHolder,
  searchPlaceHolder = 'Buscar...',
  generalSearchColumns = [],
  exportableFormats = [],
  exportFields,
  exportColumnKeys,
  exportColumnLabels,
  exportEndpoint,
  exportFilterLabels,
  exportScopes,
  isExportable = false,
  isRefreshable = false,
  isSearchable = true,
  enableBarcodeSearch = false,
  actionComponent,
  bulkActionComponents,
  filterComponents = [],
  showControls = true,
  showPagination = true,
  children,
  perPage = 10,
  pagination,
  hasMinHeight = true,
  showSelectedRows = false,
  tableKey,
  queryKeyPrefix = EMPTY_QUERY_KEY_PREFIX,
  rowHeightMode = 'regular',
  variant = 'classic',
  showRowIndex = false,
  onDataChange,
  onLoadingChange,
  onRowClick,
  ...props
}: PropsWithChildren<DatatableProps<TItem>>) => {
  const key = useMemo(() => generateTableKey(columns, endpoint, tableKey), [columns, endpoint, tableKey]);
  const savedPreferences = useMemo(() => loadDataTablePreferences(key), [key]);

  const state = useLocation().state as LocationState | null;
  const isServerSide = !!endpoint;
  const [localRows, setLocalRows] = useState(localData || []);
  const [serverPage, setServerPage] = useState<DataTablePage>();
  const remotePage = isServerSide ? serverPage : undefined;
  const data = remotePage?.rows ?? localRows;
  const dataAdditionalContent = remotePage?.additionalContent || EMPTY_ADDITIONAL_CONTENT;
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>(() => {
    if (savedPreferences.sortColumn) {
      return [{ id: savedPreferences.sortColumn, desc: savedPreferences.sortDirection === 'desc' }];
    }
    return props.sort ? [{ id: props.sort, desc: props.direction === 'desc' }] : [];
  });

  // Página e busca são espelhadas na URL (quando há `tableKey`); pageSize fica no localStorage.
  const { page, setPage, search: searchTerm, setSearch: setSearchTerm } = useUrlTableState(tableKey);
  const [urlFilter] = useUrlFilter<Record<string, unknown>>(key);
  // Nome ou lista de nomes que a tela guardou na URL; o módulo de export decide o que é vazio.
  const urlFilterLabels: Record<string, unknown> = {};
  for (const labelKey of Object.keys(exportFilterLabels ?? {})) {
    const value = urlFilter?.[labelKey];
    if (value != null) urlFilterLabels[labelKey] = value;
  }
  const [pageSize, setPageSize] = useState(() => savedPreferences.pageSize || perPage);
  const pageSizes = pagination?.pageSizes;
  const isPageSizeAllowed = pageSizes?.includes(pageSize) ?? true;
  const effectivePageSize = isPageSizeAllowed ? pageSize : perPage;
  const currentPaginationValue = useMemo<PaginationState>(
    () => ({ pageIndex: page, pageSize: effectivePageSize }),
    [page, effectivePageSize],
  );
  const debouncedSearchTerm = useDebounce(searchTerm, 500) ?? '';
  const [filter, setFilter] = useState<object | undefined>(undefined);
  const [refreshTimestamp, setRefreshTimestamp] = useState(Date.now());
  const [isInitialRenderComplete, setIsInitialRenderComplete] = useState(false);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [selectedRows, setSelectedRows] = useState<Row<object>[]>([]);
  // `rowSelection` only stores ids; keep originals in a ref-backed cache so bulk actions
  // can access DTOs for rows the user selected on other pages / before a filter change.
  const selectedRowsCacheRef = useRef<Map<string, object>>(new Map());
  const [selectedRowsData, setSelectedRowsData] = useState<object[]>([]);

  // Hook para limpar parâmetros da URL quando o componente for desmontado
  useDataTableCleanup(tableKey);

  useEffect(() => {
    setIsInitialRenderComplete(true);
  }, []);

  // Update data when localData prop changes
  useEffect(() => {
    if (!localData) return;
    setLocalRows(localData);
  }, [localData]);

  // Notify parent component when data changes
  useEffect(() => {
    if (onDataChange && data.length > 0) {
      onDataChange(data);
    }
  }, [data, onDataChange]);

  useEffect(() => {
    if (props.filter === undefined) {
      return;
    }
    setFilter(props.filter || undefined);
    if (props.refreshKey !== undefined) setRefreshTimestamp((current) => current + 1);
  }, [props.filter, props.refreshKey]);

  useEffect(() => {
    if (state?.refreshDatatable === undefined) return;
    setRefreshTimestamp(Date.now());
  }, [state, setRefreshTimestamp]);

  const updateFilter = (newFilter: Partial<Record<string, any>>) => {
    setFilter((prevFilter) => {
      const updatedFilter = !prevFilter ? { ...newFilter } : { ...prevFilter, ...newFilter };
      return updatedFilter;
    });
    setPage(0);
  };

  const clearFilter = (filterKey: any) => {
    setFilter((prevFilter) => {
      if (!prevFilter || !(filterKey in prevFilter)) return prevFilter;

      const updatedFilter = { ...prevFilter };
      delete (updatedFilter as any)[filterKey];
      return Object.keys(updatedFilter).length > 0 ? updatedFilter : undefined;
    });
    setPage(0);
  };

  /**
   * Seam único de escrita da busca: recebe tanto o input (`setSearchTerm` do contexto)
   * quanto o `onGlobalFilterChange` do react-table.
   */
  const handleSearchChange = (value: string) => {
    // `BaseSearchFilter` ecoa o termo de volta via `table.setGlobalFilter` num efeito de
    // montagem; sem esta guarda o eco seria lido como busca nova e zeraria a página da URL.
    if (value === searchTerm) return;

    setSearchTerm(value);
    // Busca nova reinicia a paginação: a página 3 do resultado anterior não existe no novo.
    if (page > 0) setPage(0);
  };

  const refresh = () => {
    setRefreshTimestamp(Date.now());
  };

  const handleSortChange = (updaterFunction: Updater<SortingState>) => {
    const newValue = functionalUpdate(updaterFunction, sorting);
    setSorting(newValue);

    if (tableKey) {
      const currentPrefs = loadDataTablePreferences(tableKey);
      if (newValue.length > 0) {
        saveDataTablePreferences(
          {
            ...currentPrefs,
            sortColumn: newValue[0].id,
            sortDirection: newValue[0].desc ? 'desc' : 'asc',
          },
          tableKey,
        );
      } else {
        saveDataTablePreferences(
          {
            ...currentPrefs,
            sortColumn: undefined,
            sortDirection: undefined,
          },
          tableKey,
        );
      }
    }
  };

  const handlePaginationChange = (updaterFunction: Updater<PaginationState>) => {
    const newValue = functionalUpdate(updaterFunction, currentPaginationValue);

    if (newValue.pageSize !== currentPaginationValue.pageSize) {
      setPageSize(newValue.pageSize);
      // Trocar o tamanho invalida o índice atual — o item que estava na página 3 mudou de lugar.
      setPage(0);

      if (tableKey) {
        const currentPrefs = loadDataTablePreferences(tableKey);
        saveDataTablePreferences(
          {
            ...currentPrefs,
            pageSize: newValue.pageSize !== perPage ? newValue.pageSize : undefined,
          },
          tableKey,
        );
      }
      return;
    }

    setPage(newValue.pageIndex);
  };

  const getSort = (sorting: SortingState) => {
    if (!sorting || sorting.length === 0) return '';
    return sorting[0].id;
  };

  const getDirection = (sorting: SortingState) => {
    if (!sorting || sorting.length === 0) return 'ASC';
    return sorting[0].desc ? 'DESC' : 'ASC';
  };

  const getExportQuery = (): ExportRequestDto['query'] => ({
    filter,
    sort: getSort(sorting),
    direction: getDirection(sorting),
    search: isServerSide ? debouncedSearchTerm : searchTerm,
  });

  const containerStyle: React.CSSProperties = hasMinHeight
    ? { minHeight: '50vh', height: '-webkit-fill-available' }
    : { height: '-webkit-fill-available' };

  const multiColumnGlobalFilter = useMemo(
    () => createMultiColumnGlobalFilter(generalSearchColumns, columns),
    [generalSearchColumns, columns],
  );

  const table = useReactTable<object>({
    data,
    columns,
    state: {
      sorting,
      globalFilter: searchTerm,
      pagination: currentPaginationValue,
      columnFilters,
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    onColumnFiltersChange: setColumnFilters,
    filterFns: {
      dateRangeFromLocalData: dateRangeFilterFn,
    },
    globalFilterFn: multiColumnGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    // Só aplica filtros localmente se não for server-side
    ...(isServerSide
      ? {
          // Para server-side, não aplicamos filtros locais
          manualFiltering: true,
          manualSorting: true,
        }
      : {
          // Para dados locais, aplicamos filtros e ordenação localmente
          getFilteredRowModel: getFilteredRowModel(),
          getSortedRowModel: getSortedRowModel(),
          getPaginationRowModel: getPaginationRowModel(),
        }),
    // Se não for server-side, o react-table fará o paginamento automático
    manualPagination: isServerSide,
    pageCount: remotePage?.totalPageCount,
    rowCount: remotePage?.totalItemCount,
    onSortingChange: handleSortChange,
    onPaginationChange: handlePaginationChange,
    onGlobalFilterChange: handleSearchChange,
    ...(isServerSide
      ? {
          getRowCanExpand,
          getExpandedRowModel: getExpandedRowModel(),
        }
      : {}),
    getRowId: getRowId ?? ((originalRow: object) => (originalRow as { id: string }).id),
  });

  useEffect(() => {
    setSelectedRows(table.getFilteredSelectedRowModel().rows);
  }, [rowSelection]);

  const selectedRowIds = useMemo(() => Object.keys(rowSelection).filter((id) => rowSelection[id]), [rowSelection]);

  useEffect(() => {
    const cache = selectedRowsCacheRef.current;
    const { rowsById } = table.getCoreRowModel();

    Object.entries(rowSelection).forEach(([id, isSelected]) => {
      if (isSelected && id in rowsById) cache.set(id, rowsById[id].original);
    });

    Array.from(cache.keys()).forEach((id) => {
      if (!rowSelection[id]) cache.delete(id);
    });

    setSelectedRowsData(Array.from(cache.values()));
  }, [rowSelection, data, table]);

  const clearSelection = () => {
    setRowSelection({});
  };

  const tableQueryKey = getDataTableQueryKey(tableKey);
  const queryKey = [
    ...queryKeyPrefix,
    ...tableQueryKey,
    currentPaginationValue,
    sorting,
    debouncedSearchTerm,
    refreshTimestamp,
    filter,
  ];
  const {
    data: serverResponse,
    error,
    isFetching: isLoading,
  } = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      if (!endpoint) return;

      const { data: response } = await list<TItem>(
        endpoint,
        currentPaginationValue.pageIndex,
        currentPaginationValue.pageSize,
        getSort(sorting),
        getDirection(sorting),
        debouncedSearchTerm,
        filter,
        signal,
      );
      // Página vazia (itens removidos, filtro encolheu o total, índice adulterado na URL):
      // só dá para saber depois da resposta — antes dela `data` está vazio por não ter carregado ainda.
      if (response.content.length === 0 && currentPaginationValue.pageIndex > 0) {
        setPage(0);
      }

      return response;
    },
    staleTime: 0,
    enabled: !!endpoint && isInitialRenderComplete,
  });

  // Mutações podem escrever no cache antes de a lista responder.
  useEffect(() => {
    if (!serverResponse) return;
    const nextPage = adaptPage ? adaptPage(serverResponse) : getDataTablePage(serverResponse);
    setServerPage(nextPage);
  }, [adaptPage, serverResponse]);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  return (
    <DataTableContext.Provider
      value={{
        columns,
        error,
        isLoading,
        filter,
        setColumnFilters,
        table,
        placeHolder,
        data,
        dataAdditionalContent,
        pageInfo: remotePage,
        pagination,
        setRefreshTimestamp,
        searchPlaceHolder,
        searchTerm,
        setSearchTerm: handleSearchChange,
        setFilter,
        updateFilter,
        clearFilter,
        refresh,
        tableKey: key,
        generalSearchColumns,
        exportableFormats,
        exportFields,
        exportColumnKeys,
        exportColumnLabels,
        exportEndpoint,
        exportFilterLabels,
        exportScopes,
        urlFilterLabels,
        listQuery: getExportQuery(),
        isExportable,
        isRefreshable,
        isSearchable,
        enableBarcodeSearch,
        actionComponent,
        bulkActionComponents,
        selectedRows,
        selectedRowIds,
        selectedRowsData,
        clearSelection,
        filterComponents,
        rowHeightMode,
        variant,
        showRowIndex,
        renderSubComponent,
        onRowClick,
      }}
    >
      <>
        {children ? children : null}
        {!children ? (
          <>
            {showControls ? <DataTableControls /> : null}
            <div>
              <DataTableBody showHeader={true} />
              {showPagination ? <DataTablePagination /> : null}
              {!!showSelectedRows && (
                <div className="d-flex flex-end mt-6 text-gray-600">
                  <p>
                    {selectedRows.length} de {table.getRowModel().rows.length} linha(s) selecionada(s)
                  </p>
                </div>
              )}
            </div>
          </>
        ) : null}
      </>
    </DataTableContext.Provider>
  );
};

const useDataTable = <TFilter extends Record<string, any> = Record<string, any>>(): DataTableContextProps<TFilter> => {
  const context = useContext(DataTableContext);
  if (!context) {
    throw new Error('useDataTable must be used within a DataTableProvider');
  }
  return context as DataTableContextProps<TFilter>;
};

export { DataTable, useDataTable };
